/**
 * Sound settings: the master switch plus per-bus volumes (master, combat SFX, UI, ambience,
 * music). Persisted in localStorage (separately from graphics settings); every storage access is
 * in try/catch. `combatAudioSettings` is a tiny shared store: the settings panel writes it, the
 * mixer (audio/mixer.ts) reads it.
 *
 * `volume` is the original "Combat volume" (the SFX bus) and keeps its storage key, so a saved
 * v1 setting still loads; the other buses default to sensible levels.
 */
import { el } from '../hud/dom';

export interface CombatAudioSettings {
  /** Master sound switch (every bus). */
  enabled: boolean;
  /** Combat / world SFX volume (0..1). */
  volume: number;
  /** Master volume (0..1), on top of the buses. */
  master: number;
  /** Interface sounds: clicks, loot, forge, social (0..1). */
  ui: number;
  /** Dungeon / hub ambience beds (0..1). */
  ambience: number;
  /** Music: fanfares and stingers (0..1). */
  music: number;
}

export type VolumeKey = 'master' | 'volume' | 'ui' | 'ambience' | 'music';

export const DEFAULT_COMBAT_AUDIO: Readonly<CombatAudioSettings> = Object.freeze({ enabled: true, volume: 0.6, master: 0.8, ui: 0.6, ambience: 0.5, music: 0.5 });
export const COMBAT_AUDIO_KEY = 'keystone.combatAudio.v1';
export const COMBAT_AUDIO_VOLUME = { min: 0, max: 1, step: 0.05 } as const;
const VOLUME_KEYS: readonly VolumeKey[] = ['master', 'volume', 'ui', 'ambience', 'music'];

export function sanitizeCombatAudio(raw: unknown): CombatAudioSettings {
  const s: CombatAudioSettings = { ...DEFAULT_COMBAT_AUDIO };
  if (!raw || typeof raw !== 'object') return s;
  const r = raw as Record<string, unknown>;
  if (typeof r['enabled'] === 'boolean') s.enabled = r['enabled'];
  for (const k of VOLUME_KEYS) {
    const v = r[k];
    if (typeof v === 'number' && Number.isFinite(v)) s[k] = Math.min(COMBAT_AUDIO_VOLUME.max, Math.max(COMBAT_AUDIO_VOLUME.min, v));
  }
  return s;
}

function defaultStorage(): Storage | null {
  try {
    return typeof localStorage === 'undefined' ? null : localStorage;
  } catch {
    return null;
  }
}

export function loadCombatAudio(storage: Pick<Storage, 'getItem'> | null = defaultStorage()): CombatAudioSettings {
  try {
    const raw = storage?.getItem(COMBAT_AUDIO_KEY);
    return sanitizeCombatAudio(raw ? JSON.parse(raw) : null);
  } catch {
    return { ...DEFAULT_COMBAT_AUDIO };
  }
}

export function saveCombatAudio(s: CombatAudioSettings, storage: Pick<Storage, 'setItem'> | null = defaultStorage()): boolean {
  try {
    if (!storage) return false;
    storage.setItem(COMBAT_AUDIO_KEY, JSON.stringify(sanitizeCombatAudio(s)));
    return true;
  } catch {
    return false;
  }
}

type Listener = (s: Readonly<CombatAudioSettings>) => void;

class CombatAudioStore {
  private value: CombatAudioSettings | null = null;
  private readonly listeners = new Set<Listener>();

  get(): Readonly<CombatAudioSettings> {
    return (this.value ??= loadCombatAudio());
  }

  set(patch: Partial<CombatAudioSettings>): void {
    this.value = sanitizeCombatAudio({ ...this.get(), ...patch });
    saveCombatAudio(this.value);
    for (const l of this.listeners) l(this.value);
  }

  /** Calls `fn` now and on every change. Returns an unsubscribe function. */
  subscribe(fn: Listener): () => void {
    this.listeners.add(fn);
    fn(this.get());
    return () => this.listeners.delete(fn);
  }
}

export const combatAudioSettings = new CombatAudioStore();

const SLIDERS: readonly { key: VolumeKey; label: string }[] = [
  { key: 'master', label: 'Master volume' },
  { key: 'volume', label: 'Combat volume' },
  { key: 'ui', label: 'Interface volume' },
  { key: 'ambience', label: 'Ambience volume' },
  { key: 'music', label: 'Music & fanfares' },
];

/** Adds a "Sound" section (switch + bus sliders + credits note) to the settings panel. Returns a sync function. */
export function mountCombatAudioControls(panel: HTMLElement, before: Node | null = null): () => void {
  const section = el('div', 'ca-settings');
  panel.insertBefore(section, before);
  el('div', 'gfx-title', section, 'Sound');
  const on = el('label', 'gfx-row check', section);
  el('span', '', on, 'Sound effects');
  const check = el('input', '', on);
  check.type = 'checkbox';
  const pct = (v: number): string => `${Math.round(v * 100)}%`;
  const rows = SLIDERS.map(({ key, label }) => {
    const row = el('label', 'gfx-row', section);
    el('span', '', row, label);
    const out = el('output', 'gfx-val', row);
    const slider = el('input', '', row);
    slider.type = 'range';
    slider.min = String(COMBAT_AUDIO_VOLUME.min);
    slider.max = String(COMBAT_AUDIO_VOLUME.max);
    slider.step = String(COMBAT_AUDIO_VOLUME.step);
    slider.addEventListener('input', () => {
      out.textContent = pct(Number(slider.value));
      combatAudioSettings.set({ [key]: Number(slider.value) });
    });
    return { key, slider, out };
  });
  check.addEventListener('change', () => {
    combatAudioSettings.set({ enabled: check.checked });
    for (const r of rows) r.slider.disabled = !check.checked;
  });
  // Credits: TomMusic (credit appreciated) and rubberduck (CC0) — see CREDITS.md.
  const credits = el('div', 'gfx-hint', section, 'Sound effects and ambience: TomMusic, "Free Fantasy 200+ SFX Pack" (tommusic.itch.io). Creature voices: rubberduck (CC0). ');
  const link = el('a', '', credits, 'Audio credits');
  link.href = `${(import.meta as { env?: { BASE_URL?: string } }).env?.BASE_URL ?? '/'}assets/audio/CREDITS.md`;
  link.target = '_blank';
  link.rel = 'noopener';
  link.setAttribute('data-sfx', 'none');

  return () => {
    const s = combatAudioSettings.get();
    check.checked = s.enabled;
    for (const r of rows) {
      r.slider.value = String(s[r.key]);
      r.out.textContent = pct(s[r.key]);
      r.slider.disabled = !s.enabled;
    }
  };
}
